import Papa from 'papaparse';
import readXlsxFile from 'read-excel-file';
import type { Fp1BenchImportPayload } from '../fp1.types';

/**
 * Parsed bench row: serial at index 2 in the spreadsheet.
 * Supports 12-column export (FW in column 4), prior 11-column, and legacy short rows.
 */
export interface ParsedFp1BenchRow {
    serial_number: string;
    bench_import: Fp1BenchImportPayload;
}

function cellString(v: unknown): string {
    if (v == null) {
        return '';
    }
    if (v instanceof Date) {
        return v.toISOString();
    }
    if (typeof v === 'string') {
        return v.trim();
    }
    if (typeof v === 'number' && Number.isFinite(v)) {
        return String(v);
    }
    return String(v).trim();
}

/**
 * Current 12-column export (1-based):
 * 1 Date/Time, 2 Current w/o FW, 3 Serial, 4 FW flashed, 5 Standby, 6 Live, 7 Charge,
 * 8 VBAT, 9 PMID, 10 IIN, 11 ICHG, 12 Error
 */
function normalizeRowNew12Layout(cells: string[]): ParsedFp1BenchRow | null {
    if (cells.length < 3) {
        return null;
    }
    const serial = cellString(cells[2]);
    if (!serial) {
        return null;
    }
    const fw_version = cells.length > 3 ? cellString(cells[3]) || null : null;
    const error_message = cells.length > 11 ? cellString(cells[11]) || null : null;
    return {
        serial_number: serial,
        bench_import: {
            tested_at: cellString(cells[0]) || null,
            current_draw: cellString(cells[1]) || null,
            fw_version,
            current_2: cells.length > 4 ? cellString(cells[4]) || null : null,
            current_3: cells.length > 5 ? cellString(cells[5]) || null : null,
            current_4: cells.length > 6 ? cellString(cells[6]) || null : null,
            vbat: cells.length > 7 ? cellString(cells[7]) || null : null,
            pmid: cells.length > 8 ? cellString(cells[8]) || null : null,
            iin: cells.length > 9 ? cellString(cells[9]) || null : null,
            ichg: cells.length > 10 ? cellString(cells[10]) || null : null,
            error_message,
        },
    };
}

/** Prior 11-column export (no FW column): error in column 11 (index 10) */
function normalizeRowOld11Layout(cells: string[]): ParsedFp1BenchRow | null {
    if (cells.length < 3) {
        return null;
    }
    const serial = cellString(cells[2]);
    if (!serial) {
        return null;
    }
    const error_message = cells.length > 10 ? cellString(cells[10]) || null : null;
    return {
        serial_number: serial,
        bench_import: {
            tested_at: cellString(cells[0]) || null,
            current_draw: cellString(cells[1]) || null,
            current_2: cells.length > 3 ? cellString(cells[3]) || null : null,
            current_3: cells.length > 4 ? cellString(cells[4]) || null : null,
            current_4: cells.length > 5 ? cellString(cells[5]) || null : null,
            vbat: cells.length > 6 ? cellString(cells[6]) || null : null,
            pmid: cells.length > 7 ? cellString(cells[7]) || null : null,
            iin: cells.length > 8 ? cellString(cells[8]) || null : null,
            ichg: cells.length > 9 ? cellString(cells[9]) || null : null,
            error_message,
        },
    };
}

/** Oldest export: 0 test date/time, 1 current, 2 serial, 3–5 currents, 6 error, 7 optional FW */
function normalizeRowLegacyLayout(cells: string[]): ParsedFp1BenchRow | null {
    if (cells.length < 3) {
        return null;
    }
    const serial = cellString(cells[2]);
    if (!serial) {
        return null;
    }
    return {
        serial_number: serial,
        bench_import: {
            tested_at: cellString(cells[0]) || null,
            current_draw: cellString(cells[1]) || null,
            current_2: cellString(cells[3]) || null,
            current_3: cellString(cells[4]) || null,
            current_4: cellString(cells[5]) || null,
            error_message: cellString(cells[6]) || null,
            fw_version: cells.length > 7 ? cellString(cells[7]) || null : null,
        },
    };
}

/** Heuristic: new 12-col sheet with blank error may trim to 11 cells; column 4 is usually a FW token. */
function looksLikeFirmwareVersionToken(s: string): boolean {
    const t = s.trim();
    if (t.length < 2 || t.length > 64) {
        return false;
    }
    return /^[\w.-]+$/.test(t) && /\d/.test(t);
}

function normalizeRow(cells: string[]): ParsedFp1BenchRow | null {
    if (cells.length >= 12) {
        return normalizeRowNew12Layout(cells);
    }
    if (cells.length === 11 && looksLikeFirmwareVersionToken(cells[3] ?? '')) {
        return normalizeRowNew12Layout([...cells, '']);
    }
    if (cells.length >= 9) {
        return normalizeRowOld11Layout(cells);
    }
    return normalizeRowLegacyLayout(cells);
}

function looksLikeHeader(cells: string[]): boolean {
    const joined = cells.map((c) => c.toLowerCase()).join('|');
    return (
        joined.includes('serial') ||
        (joined.includes('date') && joined.includes('time')) ||
        joined.includes('vbat') ||
        joined.includes('ichg') ||
        joined.includes('standby') ||
        joined.includes('firmware') ||
        joined.includes('fw') ||
        joined.includes('current')
    );
}

function stripEmptyTrailing(cells: string[]): string[] {
    let end = cells.length;
    while (end > 0 && !cells[end - 1]) {
        end -= 1;
    }
    return cells.slice(0, end);
}

/**
 * Parse a CSV or Excel (.xlsx) factory export into serial + bench metadata rows.
 * Prefer 12-column layout when the row has at least 12 cells after padding (FW in column 4).
 */
export async function parseFp1BenchImportFile(file: File): Promise<ParsedFp1BenchRow[]> {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    let rows: string[][] = [];

    if (ext === 'xlsx' || ext === 'xls') {
        const matrix = await readXlsxFile(file);
        rows = matrix.map((r) => (r as unknown[]).map((c) => cellString(c)));
    } else {
        const text = await file.text();
        const parsed = Papa.parse<string[]>(text, {
            skipEmptyLines: 'greedy',
        });
        if (parsed.errors.length && !parsed.data.length) {
            throw new Error(parsed.errors[0]?.message || 'Could not parse CSV');
        }
        rows = (parsed.data as unknown[][]).map((r) => (r as unknown[]).map((c) => cellString(c)));
    }

    const out: ParsedFp1BenchRow[] = [];
    for (let i = 0; i < rows.length; i++) {
        const cells = stripEmptyTrailing(rows[i]);
        if (!cells.length) {
            continue;
        }
        if (i === 0 && looksLikeHeader(cells)) {
            continue;
        }
        const parsedRow = normalizeRow(cells);
        if (parsedRow) {
            out.push(parsedRow);
        }
    }

    return out;
}
